import React from 'react';
import { motion } from 'framer-motion';

interface LoadingScreenProps {
  message?: string;
}

const ArtViewingScene = ({ isActive }: { isActive: boolean }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: isActive ? 1 : 0 }}
    transition={{ duration: 0.6 }}
    className="absolute inset-0 flex items-center justify-center"
  >
    <svg viewBox="0 0 200 200" className="w-32 h-32 md:w-40 md:h-40" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="frameGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      {/* Picture frame */}
      <rect x="40" y="40" width="120" height="120" fill="none" stroke="currentColor" strokeWidth="4" rx="2" className="text-primary-600" />
      {/* Inner art piece */}
      <motion.rect
        x="55"
        y="55"
        width="90"
        height="90"
        fill="url(#frameGradient)"
        rx="1"
        animate={{ opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="text-primary-600"
      />
      {/* Depth lines for 3D effect */}
      <motion.line
        x1="70"
        y1="70"
        x2="130"
        y2="130"
        stroke="currentColor"
        strokeWidth="2"
        strokeOpacity="0.4"
        className="text-primary-600"
        animate={{ strokeOpacity: [0.2, 0.5, 0.2] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
    </svg>
  </motion.div>
);

const ARHomeScene = ({ isActive }: { isActive: boolean }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: isActive ? 1 : 0 }}
    transition={{ duration: 0.6 }}
    className="absolute inset-0 flex items-center justify-center"
  >
    <svg viewBox="0 0 200 200" className="w-32 h-32 md:w-40 md:h-40" preserveAspectRatio="xMidYMid meet">
      {/* Device frame (phone) */}
      <rect x="45" y="30" width="110" height="140" fill="none" stroke="currentColor" strokeWidth="3" rx="8" className="text-primary-600" />
      {/* Screen */}
      <rect x="52" y="40" width="96" height="100" fill="none" stroke="currentColor" strokeWidth="1.5" rx="4" className="text-primary-600" />
      {/* Room outline on screen */}
      <line x1="60" y1="50" x2="140" y2="50" stroke="currentColor" strokeWidth="1.5" className="text-primary-600" />
      <line x1="60" y1="50" x2="60" y2="130" stroke="currentColor" strokeWidth="1.5" className="text-primary-600" />
      {/* AR visualization - pulsing frame */}
      <motion.rect
        x="75"
        y="65"
        width="50"
        height="50"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="text-primary-600"
        animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.3, 0.8, 0.3] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />
      {/* Home indicator */}
      <ellipse cx="100" cy="145" rx="20" ry="3" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary-600" />
    </svg>
  </motion.div>
);

const RoomsScene = ({ isActive }: { isActive: boolean }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: isActive ? 1 : 0 }}
    transition={{ duration: 0.6 }}
    className="absolute inset-0 flex items-center justify-center"
  >
    <svg viewBox="0 0 200 200" className="w-32 h-32 md:w-40 md:h-40" preserveAspectRatio="xMidYMid meet">
      {/* Room box outline */}
      <path d="M 50 100 L 50 50 L 150 50 L 150 100" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-primary-600" />
      <path d="M 50 100 L 80 130 L 150 130 L 150 100" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-primary-600" />
      <line x1="150" y1="50" x2="150" y2="100" stroke="currentColor" strokeWidth="2.5" className="text-primary-600" />
      {/* Furniture indicator - evolving */}
      <motion.g
        animate={{ opacity: [0.3, 0.8, 0.3] }}
        transition={{ duration: 2.5, repeat: Infinity, delay: 0.3 }}
      >
        {/* Bed/Furniture line */}
        <line x1="60" y1="80" x2="90" y2="80" stroke="currentColor" strokeWidth="2" className="text-primary-600" />
        <line x1="60" y1="80" x2="60" y2="95" stroke="currentColor" strokeWidth="2" className="text-primary-600" />
      </motion.g>
      {/* Window indicator */}
      <rect x="110" y="60" width="25" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary-600" />
    </svg>
  </motion.div>
);

const FrameSelector = ({ isActive }: { isActive: boolean }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: isActive ? 1 : 0 }}
    transition={{ duration: 0.6 }}
    className="absolute inset-0 flex items-center justify-center"
  >
    <svg viewBox="0 0 200 200" className="w-32 h-32 md:w-40 md:h-40" preserveAspectRatio="xMidYMid meet">
      {/* Three frame styles */}
      {/* Frame 1 - thin */}
      <motion.g
        animate={{ opacity: [1, 0.3, 0.3], x: [0, -15, -15] }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        <rect x="30" y="50" width="45" height="60" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary-600" />
        <rect x="38" y="58" width="29" height="44" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.5" className="text-primary-600" />
      </motion.g>
      {/* Frame 2 - medium */}
      <motion.g
        animate={{ opacity: [0.3, 1, 0.3], x: [0, 0, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        <rect x="75" y="45" width="50" height="70" fill="none" stroke="currentColor" strokeWidth="3" rx="2" className="text-primary-600" />
        <rect x="82" y="52" width="36" height="56" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.5" className="text-primary-600" />
      </motion.g>
      {/* Frame 3 - ornate */}
      <motion.g
        animate={{ opacity: [0.3, 0.3, 1], x: [0, 15, 15] }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        <rect x="125" y="50" width="45" height="60" fill="none" stroke="currentColor" strokeWidth="2.5" rx="1" className="text-primary-600" />
        <circle cx="135" cy="55" r="3" fill="currentColor" className="text-primary-600" opacity="0.6" />
        <circle cx="160" cy="55" r="3" fill="currentColor" className="text-primary-600" opacity="0.6" />
        <rect x="133" y="58" width="29" height="44" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.4" className="text-primary-600" />
      </motion.g>
    </svg>
  </motion.div>
);

const MatSpacingScene = ({ isActive }: { isActive: boolean }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: isActive ? 1 : 0 }}
    transition={{ duration: 0.6 }}
    className="absolute inset-0 flex items-center justify-center"
  >
    <svg viewBox="0 0 200 200" className="w-32 h-32 md:w-40 md:h-40" preserveAspectRatio="xMidYMid meet">
      {/* Frame */}
      <rect x="35" y="35" width="130" height="130" fill="none" stroke="currentColor" strokeWidth="3" className="text-primary-600" />
      {/* Mat (spacing) - animated */}
      <motion.rect
        x="50"
        y="50"
        width="100"
        height="100"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeDasharray="4 2"
        className="text-primary-600"
        animate={{ x: [50, 45, 50], y: [50, 45, 50] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />
      {/* Art piece */}
      <motion.rect
        x="70"
        y="70"
        width="60"
        height="60"
        fill="currentColor"
        className="text-primary-600"
        opacity="0.15"
        animate={{ x: [70, 75, 70], y: [70, 75, 70] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />
      {/* Spacing indicators - arrows */}
      <motion.g
        animate={{ opacity: [0.3, 0.8, 0.3] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      >
        <line x1="45" y1="100" x2="52" y2="100" stroke="currentColor" strokeWidth="1.5" className="text-primary-600" />
        <line x1="52" y1="97" x2="45" y2="100" stroke="currentColor" strokeWidth="1.5" className="text-primary-600" />
        <line x1="52" y1="103" x2="45" y2="100" stroke="currentColor" strokeWidth="1.5" className="text-primary-600" />
      </motion.g>
    </svg>
  </motion.div>
);

export default function LoadingScreen({ message = 'Loading...' }: LoadingScreenProps) {
  const scenes = [
    { Component: ArtViewingScene, label: 'Gallery' },
    { Component: ARHomeScene, label: 'AR View' },
    { Component: RoomsScene, label: 'Rooms' },
    { Component: FrameSelector, label: 'Frames' },
    { Component: MatSpacingScene, label: 'Customization' },
  ];

  const [activeScene, setActiveScene] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveScene((prev) => (prev + 1) % scenes.length);
    }, 3000); // 3 seconds per scene

    return () => clearInterval(interval);
  }, [scenes.length]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="relative w-48 h-48 mx-auto mb-8 flex items-center justify-center">
          {scenes.map((scene, index) => (
            <scene.Component key={index} isActive={activeScene === index} />
          ))}
        </div>
        <p className="text-gray-600 text-sm">{message}</p>
        <motion.div className="flex gap-2 justify-center mt-4">
          {scenes.map((_, index) => (
            <motion.div
              key={index}
              className="w-1.5 h-1.5 rounded-full bg-primary-600"
              animate={{ scale: activeScene === index ? 1.3 : 1, opacity: activeScene === index ? 1 : 0.4 }}
              transition={{ duration: 0.3 }}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
}
